import React, { useState } from "react";
import styled from "styled-components";
import { CreditCard, Users, Sparkles } from "lucide-react";
import CustomerTransaction from "../../molecules/Dashboard/CustomerTransactions/CustomerTransaction";
import TeamMemberPaymentPage from "./TeamMemberPaymentPage";
import { device } from "../../atoms/Utlis/media";

type PaymentTab = "customer" | "team-member";

const gradient = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const TabRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
  width: fit-content;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-radius: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(p) => (p.$active ? gradient : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23, 23, 23, 0.62)")};
  box-shadow: ${(p) => (p.$active ? "0 4px 12px rgba(201, 77, 170, 0.25)" : "none")};
  &:hover {
    background: ${(p) => (p.$active ? gradient : "rgba(23, 23, 23, 0.04)")};
  }
`;

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  flex-shrink: 0;
`;

const ContentArea = styled.div`
  width: 100%;
  min-height: 200px;
`;

export default function PaymentsPage() {
  const [activeTab, setActiveTab] = useState<PaymentTab>("customer");

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <div className="flex flex-col">
            <HeroBadge>
              <Sparkles size={13} />
              Finance hub
            </HeroBadge>
            <PageTitle className="linear-text">Payments</PageTitle>
            <PageSubtitle>
              Manage customer payments and team member payouts in one place.
            </PageSubtitle>
          </div>
        </HeroContent>
      </Hero>

      <TabRow>
        <Tab
          $active={activeTab === "customer"}
          onClick={() => setActiveTab("customer")}
        >
          <CreditCard size={18} strokeWidth={2.4} />
          Customer Payment
        </Tab>
        <Tab
          $active={activeTab === "team-member"}
          onClick={() => setActiveTab("team-member")}
        >
          <Users size={18} strokeWidth={2.4} />
          Team Member Payment
        </Tab>
      </TabRow>

      <Panel>
        <ContentArea>
          {activeTab === "customer" && <CustomerTransaction />}
          {activeTab === "team-member" && <TeamMemberPaymentPage />}
        </ContentArea>
      </Panel>
    </PageContainer>
  );
}
